import React, { useEffect, useState } from "react";
import { MapPin, Car, Clock, Video, Phone, Mail, Sunset } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

const shortDate = (iso) => { try { return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }); } catch { return iso; } };

export default function Visit() {
  const [sabbath, setSabbath] = useState(null);
  useEffect(() => { api.get("/settings/sabbath").then((r) => setSabbath(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="visit-page">
      <section className="bg-[#003B5C] py-28 lg:py-36 relative grain-overlay">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-6">Plan Your Visit</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-white max-w-4xl">
            We'd love to meet you.
          </h1>
          <p className="mt-8 text-lg sm:text-xl text-white/75 max-w-2xl leading-relaxed">
            Whether it's your first time or your hundredth, here's everything you need for a warm welcome this Sabbath.
          </p>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 space-y-6">
              <div data-testid="visit-address-card" className="bg-[#F7F8FA] border border-gray-100 p-8 rounded-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#003B5C] rounded-sm flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} className="text-[#D4A437]" />
                  </div>
                  <div>
                    <div className="text-[#D4A437] text-xs font-bold uppercase tracking-[0.2em] mb-2">Address</div>
                    <h3 className="font-display text-2xl font-bold text-[#003B5C]">271 Walton St</h3>
                    <p className="text-[#1E1E1E]/75 mt-1">Richardson, TX 75081</p>
                    <a
                      href="https://www.google.com/maps/dir/?api=1&destination=271+Walton+St,+Richardson,+TX+75081"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="visit-directions-link"
                      className="inline-block mt-4 text-sm text-[#003B5C] font-semibold border-b-2 border-[#D4A437] pb-0.5 hover:gap-3"
                    >
                      Get directions →
                    </a>
                  </div>
                </div>
              </div>

              <div data-testid="visit-parking-card" className="bg-[#003B5C] text-white p-8 rounded-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D4A437] rounded-sm flex items-center justify-center flex-shrink-0">
                    <Car size={20} className="text-white" />
                  </div>
                  <div>
                    <div className="text-[#D4A437] text-xs font-bold uppercase tracking-[0.2em] mb-2">Parking</div>
                    <h3 className="font-display text-2xl font-bold">Please park at Lot C</h3>
                    <p className="text-white/70 mt-2">Greeters will be there to welcome you and answer any questions.</p>
                  </div>
                </div>
              </div>

              <div data-testid="visit-times-card" className="bg-[#F7F8FA] border border-gray-100 p-8 rounded-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#003B5C] rounded-sm flex items-center justify-center flex-shrink-0">
                    <Clock size={20} className="text-[#D4A437]" />
                  </div>
                  <div className="flex-1">
                    <div className="text-[#D4A437] text-xs font-bold uppercase tracking-[0.2em] mb-3">Saturday Services</div>
                    <div className="space-y-2 text-[#1E1E1E]">
                      <div className="flex justify-between border-b border-gray-200 pb-2">
                        <span>Sabbath School</span>
                        <span className="font-mono font-bold text-[#003B5C]">9:30 AM</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Divine Worship</span>
                        <span className="font-mono font-bold text-[#003B5C]">11:15 AM</span>
                      </div>
                    </div>
                    {sabbath && (
                      <div data-testid="visit-sabbath-times" className="mt-5 pt-4 border-t border-gray-200">
                        <div className="flex items-center gap-1.5 text-[#003B5C] text-xs font-bold uppercase tracking-[0.18em] mb-2">
                          <Sunset size={13} className="text-[#D4A437]" /> This Week's Sabbath
                        </div>
                        <div className="space-y-1.5 text-sm text-[#1E1E1E]">
                          <div className="flex justify-between gap-3">
                            <span>Sabbath begins · Fri {shortDate(sabbath.friday_date)}</span>
                            <span data-testid="visit-sabbath-begins" className="font-mono font-bold text-[#003B5C]">{sabbath.friday_sunset}</span>
                          </div>
                          <div className="flex justify-between gap-3">
                            <span>Sabbath ends · Sat {shortDate(sabbath.saturday_date)}</span>
                            <span data-testid="visit-sabbath-ends" className="font-mono font-bold text-[#003B5C]">{sabbath.saturday_sunset}</span>
                          </div>
                        </div>
                        <div className="text-[11px] text-[#1E1E1E]/50 mt-2">Sunset times · {sabbath.source}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div data-testid="visit-zoom-card" className="bg-white border-2 border-[#D4A437] p-8 rounded-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D4A437] rounded-sm flex items-center justify-center flex-shrink-0">
                    <Video size={20} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="text-[#D4A437] text-xs font-bold uppercase tracking-[0.2em] mb-2">Worship Online</div>
                    <h3 className="font-display text-xl font-bold text-[#003B5C] mb-3">Join via Zoom</h3>
                    <div className="space-y-1 text-sm text-[#1E1E1E]/80">
                      <div>Meeting ID: <span className="font-mono font-bold text-[#003B5C]">861 0700 4821</span></div>
                      <div>Passcode: <span className="font-mono font-bold text-[#003B5C]">2022</span></div>
                    </div>
                    <a
                      href="https://us02web.zoom.us/j/86107004821?pwd=OE9hcjJXRTllSkdPQXlQcGhLU1J2UT09"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="visit-zoom-link"
                      className="inline-flex items-center gap-2 mt-5 bg-[#003B5C] text-white px-6 py-3 rounded-sm font-medium hover:bg-[#002A42] transition-all text-sm"
                    >
                      <Video size={16} /> Open Zoom
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 space-y-6">
              <div data-testid="visit-map" className="rounded-sm overflow-hidden border border-gray-100 h-80 lg:h-[420px] bg-[#F7F8FA]">
                <iframe
                  title="Church Location"
                  src="https://www.google.com/maps?q=271+Walton+St,+Richardson,+TX+75081&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

              <div className="bg-[#F7F8FA] border border-gray-100 p-6 rounded-sm">
                <div className="text-[#D4A437] text-xs font-bold uppercase tracking-[0.2em] mb-4">Reach Out</div>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-center gap-3">
                    <Phone size={16} className="text-[#003B5C]" />
                    <a href="tel:6825647119" data-testid="visit-phone" className="text-[#1E1E1E]/80 hover:text-[#003B5C]">682-564-7119</a>
                  </li>
                  <li className="flex items-center gap-3">
                    <Mail size={16} className="text-[#003B5C]" />
                    <a href="mailto:egsda01@gmail.com" data-testid="visit-email" className="text-[#1E1E1E]/80 hover:text-[#003B5C]">egsda01@gmail.com</a>
                  </li>
                </ul>
                <Link to="/contact" data-testid="visit-contact-cta" className="block text-center mt-5 bg-[#003B5C] text-white py-3 rounded-sm font-medium hover:bg-[#002A42] transition-all text-sm">
                  Send Us a Message
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
